import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bars3Icon, BellIcon, UserIcon, ArrowRightOnRectangleIcon, Cog6ToothIcon, BuildingOfficeIcon, ChevronDownIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import LanguageSwitcher from '../ui/LanguageSwitcher';

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, isSuperAdmin, currentCompany, getCompanies, switchCompany, returnToSuperAdmin, isSuperAdminMode } = useAuth();
  const { isRTL } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleCompanySwitch = (companyId) => {
    switchCompany(companyId);
    setCompanyMenuOpen(false);
    // Navigate to company dashboard using React Router
    navigate('/dashboard');
  };

  const handleReturnToSuperAdmin = () => {
    returnToSuperAdmin();
    setCompanyMenuOpen(false);
    // Navigate back to super admin dashboard using React Router
    navigate('/super-admin/dashboard');
  };

  const companies = isSuperAdmin() ? getCompanies() : [];

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          
          {/* Super Admin viewing company indicator */}
          {isSuperAdmin() && currentCompany && !isSuperAdminMode && (
            <div className="hidden lg:flex items-center ml-4 px-3 py-1 bg-blue-50 rounded-full">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-blue-600 font-medium">
                  Viewing as Super Admin: {currentCompany.name}
                </span>
                <button
                  onClick={handleReturnToSuperAdmin}
                  className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-100"
                  title="Return to Super Admin Dashboard"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Actions */}
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
          {/* Company Switcher - Super Admin Only */}
          {isSuperAdmin() && (
            <div className="relative">
              <button
                onClick={() => setCompanyMenuOpen(!companyMenuOpen)}
                className={`flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 ${
                  isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                }`}
              >
                <BuildingOfficeIcon className="w-4 h-4" />
                <span>
                  {currentCompany ? currentCompany.name : 'Select Company'}
                </span>
                <ChevronDownIcon className="w-4 h-4" />
              </button>

              {companyMenuOpen && (
                <div className={`absolute mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 ${
                  isRTL ? 'left-0' : 'right-0'
                }`}>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Switch Company
                  </div>
                  <div className="border-t border-gray-100">
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => handleCompanySwitch(company.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                          currentCompany?.id === company.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {company.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {company.plan} plan • {company.status}
                            </div>
                          </div>
                          {currentCompany?.id === company.id && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 px-4 py-2">
                    <button
                      onClick={handleReturnToSuperAdmin}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                    >
                      <ArrowLeftIcon className="w-3 h-3" />
                      <span>Back to Super Admin</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications */}
          <button className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100">
            <BellIcon className="w-5 h-5" />
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`flex items-center p-2 rounded-md text-gray-700 hover:bg-gray-100 ${
                isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'
              }`}
            >
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-white" />
              </div>
              <div className={`hidden md:block ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="text-sm font-medium">
                  {user?.email}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.role || 'User'}
                  {isSuperAdmin() && currentCompany && (
                    <span className="text-blue-600"> • Viewing {currentCompany.name}</span>
                  )}
                </div>
              </div>
            </button>

            {/* User Dropdown */}
            {userMenuOpen && (
              <div className={`absolute mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 ${
                isRTL ? 'left-0' : 'right-0'
              }`}>
                <a
                  href="/settings"
                  className={`flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                    isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                  }`}
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  <span>{t('navigation.settings')}</span>
                </a>
                {isSuperAdmin() && currentCompany && (
                  <button
                    onClick={handleReturnToSuperAdmin}
                    className={`flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-gray-100 ${
                      isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                    }`}
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    <span>Return to Super Admin</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${
                    isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                  }`}
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  <span>{t('auth.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 